/**
 * Owner: Masa Bayu
 * File: pdf-object-order.ts
 * Date:Time: 2026-09-30 14:10
 * Description: Page objects, in content-stream order, into Document IR.
 */

import type { QXDocumentIR } from '@qx/qx-document-ir';
import { advanceUnitsForText, resolvePdfFontGlyphWidths } from './pdf-glyph-widths';
import { parsePdfLayoutToIR } from './pdf-layout-parse';

export type QXPdfOrderBox = {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

type Shared = {
  readonly sourceObjectId: string;
  readonly sourcePage: number;
  readonly sourceOrder: number;
  readonly zOrder: number;
  readonly bbox: QXPdfOrderBox;
};

export type QXPdfOrderedImage = Shared & {
  readonly type: 'image';
  readonly assetId: string;
  readonly jpeg: Uint8Array;
};

export type QXPdfOrderedText = Shared & {
  readonly type: 'text';
  readonly text: string;
  readonly fontName: string;
  readonly fontSize: number;
  readonly bold: boolean;
  readonly italic: boolean;
};

export type QXPdfOrderedRaster = Shared & {
  readonly type: 'raster';
  readonly assetId: string;
  readonly samples: string;
  readonly maskSamples: string;
};

export type QXPdfOrderedObject = QXPdfOrderedImage | QXPdfOrderedText | QXPdfOrderedRaster;

export type QXPdfObjectOrderImport = {
  readonly document: QXDocumentIR;
  readonly objects: readonly QXPdfOrderedObject[];
};

const KEEP = 3;
const latin1 = new TextDecoder('latin1');

type ImageHit = {
  readonly at: number;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly x: number;
  readonly pdfY: number;
};

type TextHit = {
  readonly at: number;
  readonly showingIndex: number;
  readonly font: string;
  readonly size: number;
  readonly x: number;
  readonly pdfY: number;
  readonly text: string;
};

function bytesOf(text: string): Uint8Array {
  return Uint8Array.from(text, (char) => char.charCodeAt(0));
}

function indexOfBytes(haystack: Uint8Array, needle: Uint8Array, from = 0): number {
  const last = haystack.length - needle.length;
  for (let index = from; index <= last; index += 1) {
    let matched = true;
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (haystack[index + offset] !== needle[offset]) {
        matched = false;
        break;
      }
    }
    if (matched) return index;
  }
  return -1;
}

function textOf(file: Uint8Array, start: number, end: number): string {
  return latin1.decode(file.subarray(start, end));
}

function readStream(file: Uint8Array, id: number): { readonly dict: string; readonly stream: Uint8Array | null } {
  const marked = indexOfBytes(file, bytesOf(`\n${String(id)} 0 obj`));
  const at = marked >= 0 ? marked + 1 : 0;
  if (marked < 0 && indexOfBytes(file, bytesOf(`${String(id)} 0 obj`)) !== 0) {
    throw new Error(`PDF object ${String(id)} is missing`);
  }
  const slice = file.subarray(at);
  const streamAt = indexOfBytes(slice, bytesOf('stream'));
  const endAt = indexOfBytes(slice, bytesOf('endobj'));
  if (streamAt < 0 || (endAt >= 0 && endAt < streamAt)) {
    return { dict: textOf(slice, 0, endAt < 0 ? slice.length : endAt), stream: null };
  }
  const dict = textOf(slice, 0, streamAt);
  const length = dict.match(/\/Length\s+(\d+)/);
  if (!length?.[1]) throw new Error(`PDF object ${String(id)} stream has no length`);
  let dataAt = streamAt + 'stream'.length;
  if (slice[dataAt] === 0x0d) dataAt += 1;
  if (slice[dataAt] === 0x0a) dataAt += 1;
  return { dict, stream: slice.subarray(dataAt, dataAt + Number(length[1])) };
}

function pdfName(raw: string): string {
  return raw.replace(/#([0-9A-Fa-f]{2})/g, (_, hex: string) => String.fromCharCode(Number.parseInt(hex, 16)));
}

function literalOf(block: string): string {
  let text = '';
  for (const item of block.matchAll(/\((?:\\.|[^)\\])*\)/g)) {
    text += (item[0] ?? '').slice(1, -1).replace(/\\(.)/g, '$1');
  }
  return text;
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index] ?? 0);
  return btoa(binary);
}

function imageHits(content: string): ImageHit[] {
  const hits: ImageHit[] = [];
  for (const item of content.matchAll(/([-\d.]+)\s+0\s+0\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+cm\s*\/(Image\d+)\s+Do/g)) {
    const width = Number(item[1]);
    const height = Number(item[2]);
    if (width < 80 || height < 80) continue;
    hits.push({
      at: item.index ?? 0,
      name: item[5] ?? '',
      width,
      height,
      x: Number(item[3]),
      pdfY: Number(item[4]),
    });
  }
  return hits;
}

function textHits(content: string): TextHit[] {
  const hits: TextHit[] = [];
  for (const item of content.matchAll(/BT([\s\S]*?)ET/g)) {
    const block = item[1] ?? '';
    const face = block.match(/\/(F\d+)\s+([\d.]+)\s+Tf/);
    const place = block.match(/1\s+0\s+0\s+1\s+([-\d.]+)\s+([-\d.]+)\s+Tm/);
    const text = literalOf(block).trim();
    if (!face?.[1] || !face[2] || !place?.[1] || !place[2] || !/[A-Za-z]{3,}/.test(text)) continue;
    const at = item.index ?? 0;
    hits.push({
      at,
      showingIndex: [...content.slice(0, at).matchAll(/BT/g)].length,
      font: face[1],
      size: Number(face[2]),
      x: Number(place[1]),
      pdfY: Number(place[2]),
      text,
    });
  }
  return hits;
}

/**
 * Reads substantial objects on one uncompressed page in content-stream order.
 * A limit of three preserves the order proof. A null limit reads the page.
 */
function readPdfObjectOrder(
  bytes: Uint8Array,
  pageNumber: number,
  limit: number | null,
  sourceHash: string,
): QXPdfObjectOrderImport {
  if (!Number.isInteger(pageNumber) || pageNumber < 1) throw new Error('pageNumber must be a positive integer');
  const file = Uint8Array.from(bytes);
  const boxAt = indexOfBytes(file, bytesOf('/MediaBox'));
  if (boxAt < 0) throw new Error('PDF page has no MediaBox');
  const box = textOf(file, boxAt, boxAt + 120).match(/\/MediaBox\s*\[\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)\s*\]/);
  const pageWidth = Number(box?.[1] ?? 0);
  const pageHeight = Number(box?.[2] ?? 0);
  if (pageWidth <= 0 || pageHeight <= 0) throw new Error('PDF MediaBox is empty');
  const near = textOf(file, Math.max(0, boxAt - 800), Math.min(file.length, boxAt + 1500));
  const contentsId = Number(near.match(/\/Contents\s+(\d+)\s+0\s+R/)?.[1] ?? 0);
  if (!contentsId) throw new Error('PDF page has no content stream');
  const content = readStream(file, contentsId);
  if (/\/Filter\b/.test(content.dict) || !content.stream) throw new Error('PDF content stream is not plain text');
  const stream = textOf(content.stream, 0, content.stream.length);
  const ranked = [
    ...imageHits(stream).map((hit) => ({ kind: 'image' as const, hit })),
    ...textHits(stream).map((hit) => ({ kind: 'text' as const, hit })),
  ]
    .sort((left, right) => left.hit.at - right.hit.at);
  const pageObjects = limit === null ? ranked : ranked.slice(0, limit);
  if (pageObjects.length === 0 || (limit !== null && pageObjects.length < limit)) {
    throw new Error('Uncompressed page has fewer clear objects than the proof requires');
  }
  const objects: QXPdfOrderedObject[] = [];
  const blocks: {
    readonly id: string;
    readonly kind: 'image' | 'text';
    readonly assetId?: string;
    readonly text: string;
    readonly bbox: QXPdfOrderBox;
    readonly lines?: {
      readonly text: string;
      readonly bbox: QXPdfOrderBox;
      readonly runs: readonly {
        readonly text: string;
        readonly fontName: string;
        readonly fontSize: number;
        readonly bold: boolean;
        readonly italic: boolean;
        readonly bbox: QXPdfOrderBox;
      }[];
    }[];
  }[] = [];
  pageObjects.forEach((item, sourceOrder) => {
    if (item.kind === 'image') {
      const objectId = Number(near.match(new RegExp(`\\/${item.hit.name}\\s+(\\d+)\\s+0\\s+R`))?.[1] ?? 0);
      if (!objectId) throw new Error(`PDF image ${item.hit.name} has no object`);
      const image = readStream(file, objectId);
      const head = image.stream;
      if (!head || head[0] !== 0xff || head[1] !== 0xd8 || head[2] !== 0xff) {
        throw new Error(`PDF object ${String(objectId)} is not a JPEG image`);
      }
      const jpeg = Uint8Array.from(head);
      const bbox = {
        x: item.hit.x,
        y: pageHeight - item.hit.pdfY - item.hit.height,
        width: item.hit.width,
        height: item.hit.height,
      };
      const sourceObjectId = `obj_${String(objectId)}`;
      const assetId = `asset_${String(objectId)}`;
      objects.push({ type: 'image', sourceObjectId, assetId, sourcePage: pageNumber, sourceOrder, zOrder: sourceOrder, bbox, jpeg });
      blocks.push({ id: sourceObjectId, kind: 'image', assetId, text: `data:image/jpeg;base64,${base64(jpeg)}`, bbox });
      return;
    }
    const fontId = Number(near.match(new RegExp(`\\/${item.hit.font}\\s+(\\d+)\\s+0\\s+R`))?.[1] ?? 0);
    if (!fontId) throw new Error(`PDF font ${item.hit.font} has no object`);
    const font = readStream(file, fontId).dict;
    const base = font.match(/\/BaseFont\s*\/([^\s/]+)/)?.[1];
    if (!base) throw new Error('PDF font has no BaseFont');
    const fontName = pdfName(base);
    const glyph = resolvePdfFontGlyphWidths(font, (id) => readStream(file, id).dict);
    const units = advanceUnitsForText(glyph, item.hit.text);
    const bbox = {
      x: item.hit.x,
      y: pageHeight - item.hit.pdfY - item.hit.size,
      width: (units * item.hit.size) / 1000,
      height: item.hit.size,
    };
    const sourceObjectId = `txt_${item.hit.font}_${String(item.hit.showingIndex)}`;
    const bold = fontName.endsWith(',Bold') || fontName.endsWith('-Bold') || fontName.endsWith(' Bold');
    const italic = /Italic/.test(fontName);
    objects.push({
      type: 'text',
      sourceObjectId,
      text: item.hit.text,
      sourcePage: pageNumber,
      sourceOrder,
      zOrder: sourceOrder,
      bbox,
      fontName,
      fontSize: item.hit.size,
      bold,
      italic,
    });
    blocks.push({
      id: sourceObjectId,
      kind: 'text',
      text: item.hit.text,
      bbox,
      lines: [
        {
          text: item.hit.text,
          bbox,
          runs: [{ text: item.hit.text, fontName, fontSize: item.hit.size, bold, italic, bbox }],
        },
      ],
    });
  });
  return {
    objects,
    document: parsePdfLayoutToIR({
      sourceHash,
      pages: [{ pageNumber, width: pageWidth, height: pageHeight, blocks }],
    }),
  };
}

/** First three substantial objects, in paint order. */
export function importPdfObjectOrder(bytes: Uint8Array, pageNumber: number): QXPdfObjectOrderImport {
  return readPdfObjectOrder(bytes, pageNumber, KEEP, 'sha256:qxeditor-poc-source-object-order-01');
}

/** Every substantial image and clear text on the page, in paint order. */
export function importPdfPage(bytes: Uint8Array, pageNumber: number): QXPdfObjectOrderImport {
  return readPdfObjectOrder(bytes, pageNumber, null, 'sha256:qxeditor-poc-page-mirror-01');
}
